/**
 * JS-1083: "Ask Claude" handoff -- builds the copy-to-clipboard prompt string
 * for a Desk idea (the screener drawer, an edition card), naming both the
 * idea's `ledger_id` AND the exact MCP tool name
 * (`trade_ideas_get_by_ledger_id`, services/apis/lh_mcp/tools/trade_ideas.py)
 * so Claude calls the right tool and looks the idea up itself, rather than
 * the Portal trying to embed a full analysis inline.
 *
 * ADR-011: the prompt states FACTS ONLY, never advice/conviction language --
 * no "you should", no "strong buy", no directional recommendation beyond the
 * idea's own already-published direction/archetype label. Mirrors
 * `tickerContextLink.ts`'s and `deskIdeaWatchPrefill.ts`'s own honest-absence
 * convention: a field the row/entry does not have is simply omitted from the
 * prompt line, never fabricated or rendered as "N/A".
 *
 * v1 is copy/paste only (ConnectorTab.tsx's precedent) -- no in-app chat, no
 * OS deep link.
 */

import { archetypeLabel, projectedDisplayExpiry, projectedDisplayStrikes } from "./screenerLabels";
import type { MultiStrategyScreenEntry } from "./contracts";

/**
 * JS-1091 round 2 (FIX 5): the signal score named in the Ask-Claude prompt
 * is an EXPRESSION-SPECIFIC claim (it is presented as this exact
 * strategy/strikes/entry's own quality) -- so it must be sourced from
 * `entry.expression_best_idea` (server-merged onto the entry, see
 * services/apis/domains/trade_ideas/screener_reads.py's
 * `_merge_expression_ids`), never the ROW-level `best_idea_score`/
 * `best_idea`, which is keyed to the row's PRIMARY expression and would
 * silently misattribute the score to a DIFFERENT expression than the one
 * every other fact in this same prompt describes. Rounds to a 0-100
 * rating; `null` (the fact is OMITTED from the prompt, never substituted
 * with the row-level score) when this entry carries no independent
 * breakdown -- e.g. a legacy row produced before per-expression scoring
 * existed.
 */
function signalScoreLabel(
  entry: Pick<MultiStrategyScreenEntry, "expression_best_idea">,
): string | null {
  const nested = (
    entry.expression_best_idea as { quality_score?: string | null } | null | undefined
  )?.quality_score;
  if (nested == null || nested === "") return null;
  const parsed = Number(nested);
  if (!Number.isFinite(parsed)) return null;
  const rating = Math.round(Math.max(0, Math.min(1, parsed)) * 100);
  return `${rating}/100`;
}

export interface AskClaudePromptInput {
  ledgerId: string;
  ticker: string;
  entry: Pick<
    MultiStrategyScreenEntry,
    | "archetype"
    | "entry_type"
    | "natural_entry_per_share"
    | "strikes"
    | "expiry"
    | "expression_best_idea"
    // JS-1094 (demo blocker FIX 4): needed for projectedDisplayStrikes /
    // projectedDisplayExpiry below -- a real, priced CREDIT entry (a
    // bull-put/bear-call/condor) never populates canonical `strikes`/
    // `expiry` at all (only the debit pipeline does), so this prompt must
    // fall through to the SAME `priced_legs` projection the screener/edition
    // surfaces already use, or a credit idea's copied prompt silently omits
    // its own strikes/expiry facts.
    | "priced_legs"
  >;
  direction?: string | null;
}

// FIX 5 (adversarial review): render an entry-side word ONLY for a
// recognized `entry_type` value -- an unrecognized or absent value must
// OMIT the entry fact entirely, never fall back to a guessed/default side
// (the reviewer's probe: a null `entry_type` was previously mislabeled
// "Net debit").
const ENTRY_SIDE_LABELS: Record<string, string> = {
  NET_DEBIT: "Net debit",
  NET_CREDIT: "Net credit",
};

/**
 * Build the "Ask Claude" prompt string for one Desk idea. Facts appear in a
 * fixed order (ticker, strategy, direction, strikes/expiry, entry, signal)
 * so the copied text reads consistently across the screener drawer and
 * edition cards -- each fact clause is appended only when the underlying
 * value is actually present (honest-absence, never a guessed/blank fact).
 */
export function buildAskClaudePrompt(input: AskClaudePromptInput): string {
  const { ledgerId, ticker, entry, direction } = input;
  const facts: string[] = [];

  const strategyLabel = entry.archetype ? archetypeLabel(entry.archetype) : null;
  if (strategyLabel) {
    facts.push(`Strategy: ${strategyLabel}`);
  }
  if (direction) {
    facts.push(`Direction: ${direction}`);
  }
  // JS-1094 (demo blocker FIX 4): projected, not raw -- a priced credit
  // entry's canonical `strikes`/`expiry` are legitimately empty (see
  // `AskClaudePromptInput.entry`'s doc comment above); projectedDisplayStrikes/
  // projectedDisplayExpiry fall through to `entry.priced_legs` in that case,
  // the SAME projection TickerDashboard's banner/overlays and the screener/
  // edition drawers already use (screenerLabels.ts), so this prompt never
  // diverges from what the visitor is already looking at on-screen.
  const displayStrikes = projectedDisplayStrikes(entry);
  if (displayStrikes.length > 0) {
    facts.push(`Strikes: ${displayStrikes.join(" / ")}`);
  }
  const displayExpiry = projectedDisplayExpiry(entry);
  if (displayExpiry) {
    facts.push(`Expiry: ${displayExpiry}`);
  }
  const entrySide = entry.entry_type ? ENTRY_SIDE_LABELS[entry.entry_type] : undefined;
  if (entrySide && entry.natural_entry_per_share != null) {
    facts.push(`${entrySide} entry: $${entry.natural_entry_per_share}/sh`);
  }
  const signalLabel = signalScoreLabel(entry);
  if (signalLabel) {
    facts.push(`Signal score: ${signalLabel}`);
  }

  const factsLine = facts.length > 0 ? ` Facts so far -- ${facts.join(", ")}.` : "";
  // FIX 5: omit the ticker parenthetical entirely when ticker is absent --
  // never render a bare "()" (the reviewer's null-ticker probe).
  const tickerClause = ticker ? ` (${ticker})` : "";

  // JS-1088 (owner copy-voice rule): the raw ti_ledger_sha256 id is a
  // FUNCTIONAL parameter Claude's tool call needs (the exact lookup key),
  // not decorative provenance -- it cannot be dropped or hidden in a
  // tooltip the way the ticket's other raw-hash offenders can. What must
  // change is HOW it reads: presented as a named parameter binding
  // ("ledger_id=...") rather than woven into running prose as the sentence's
  // own grammatical object ("look up trade idea <hash> and help me...").
  return (
    `Using the LinkedHash MCP tool trade_ideas_get_by_ledger_id with `
    + `ledger_id=${ledgerId}, look up this trade idea${tickerClause} and `
    + `help me understand it.${factsLine}`
  );
}
